import { FC, useEffect } from "react";
import { useIntl } from "react-intl";

import { CloseFullscreen, Minimize, OpenInFull, UnfoldMore } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";

import type { SpeedScreenFormLayout } from "@framework/provider-settings";

import { SpeedScreenFormProvider, useSpeed } from "../../../../../context/speed-context";
import { MultiAnalysisPanel } from "../../multi-analysis/components/panel";
import {
  SPEED_SCREEN_FORM_DRAG_TYPE,
  type SpeedScreenForm,
  type useSpeedScreenForms,
} from "../hooks/use-speed-screen-forms";
import type { ISpeedScreenFormStrategy } from "../screen-forms/types";
import { resolveSpeedTimePanelStrategy } from "./time-panel";

interface ISpeedGraphicAreaProps {
  layout: SpeedScreenFormLayout;
  strategies: readonly ISpeedScreenFormStrategy[];
  screenForms: ReturnType<typeof useSpeedScreenForms>;
  draggedForm: SpeedScreenForm | null;
  onDragEnd: () => void;
}

const FormWindow: FC<{
  strategies: readonly ISpeedScreenFormStrategy[];
  formId: string | null;
  windowId: number;
  canDrop: boolean;
  fallbackFormId: SpeedScreenForm | null;
}> = ({ strategies, formId, windowId, canDrop, fallbackFormId }) => {
  const { formatMessage } = useIntl();
  const strategy = strategies.find(item => item.id === formId);

  if (!strategy) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          bgcolor: canDrop ? "action.hover" : "background.paper",
        }}
      >
        <Typography color="textSecondary" align="center" sx={{ fontSize: 13 }}>
          {formatMessage({ id: "pages.speed.selectScreenForm" })}
        </Typography>
      </Box>
    );
  }

  const dataFormId = strategy.renderTimePanel ? strategy.id : fallbackFormId;
  const content = strategy.render({ windowId });
  return (
    <Box sx={{ height: "100%", minHeight: 0, bgcolor: canDrop ? "action.hover" : "background.paper" }}>
      {dataFormId ? <SpeedScreenFormProvider formId={dataFormId}>{content}</SpeedScreenFormProvider> : content}
    </Box>
  );
};

export const SpeedGraphicArea: FC<ISpeedGraphicAreaProps> = ({
  layout,
  strategies,
  screenForms,
  draggedForm,
  onDragEnd,
}) => {
  const { formatMessage } = useIntl();
  const { setActiveMeasureType, setActiveMeasureWindow } = useSpeed();
  const { windows, activeWindowId, expandedWindowId, placeForm, selectWindow, toggleExpand, toggleCollapse } =
    screenForms;
  const columnCount = layout === "2H" || layout === "4" ? 2 : 1;
  const rowCount = layout === "2V" || layout === "4" ? 2 : 1;
  const activeForm = activeWindowId !== null ? windows[activeWindowId]?.form : null;
  const placedStrategies = windows
    .map(window => strategies.find(strategy => strategy.id === window.form))
    .filter((strategy): strategy is ISpeedScreenFormStrategy => strategy !== undefined);
  const fallbackFormId = resolveSpeedTimePanelStrategy(placedStrategies, activeForm)?.id ?? null;
  const collapsedColumns = Array.from({ length: columnCount }, (_, column) =>
    windows.filter((_, index) => index % columnCount === column).every(window => window.collapsed),
  );
  const gridColumns =
    expandedWindowId !== null
      ? "minmax(0, 1fr)"
      : columnCount > 1 && collapsedColumns.some(Boolean)
        ? collapsedColumns.map(collapsed => (collapsed ? "100px" : "minmax(0, 1fr)")).join(" ")
        : `repeat(${columnCount}, minmax(0, 1fr))`;

  useEffect(() => {
    const measureType = strategies.find(strategy => strategy.id === activeForm)?.measureType ?? null;
    setActiveMeasureType(measureType);
    setActiveMeasureWindow(
      measureType && activeWindowId !== null
        ? {
            templateId: null,
            cellId: `speed-${activeWindowId}`,
            row: Math.floor(activeWindowId / columnCount),
            column: activeWindowId % columnCount,
            measureType,
          }
        : null,
    );
  }, [activeForm, activeWindowId, columnCount, setActiveMeasureType, setActiveMeasureWindow, strategies]);

  return (
    <Box
      role="group"
      aria-label={formatMessage({ id: "pages.speed.workspace" })}
      sx={{
        height: "100%",
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: gridColumns,
        gridTemplateRows:
          expandedWindowId !== null
            ? "minmax(0, 1fr)"
            : Array.from({ length: rowCount }, (_, row) =>
                windows.slice(row * columnCount, (row + 1) * columnCount).every(window => window.collapsed)
                  ? "48px"
                  : "minmax(48px, 1fr)",
              ).join(" "),
        gap: 1,
        overflow: "auto",
      }}
    >
      {windows.map((window, windowId) => {
        const strategy = strategies.find(item => item.id === window.form);
        const titleId = strategy?.titleId ?? "pages.speed.emptyWindow";
        const label = formatMessage({ id: "pages.speed.window" }, { number: windowId + 1 });
        const canDrop = draggedForm !== null && window.form === null;

        return (
          <Box
            key={windowId}
            role="region"
            aria-label={label}
            tabIndex={0}
            data-speed-window={windowId}
            data-form={window.form ?? ""}
            onClick={() => selectWindow(windowId)}
            onFocus={() => selectWindow(windowId)}
            onDragOver={event => {
              if (canDrop && event.dataTransfer.types.includes(SPEED_SCREEN_FORM_DRAG_TYPE)) {
                event.preventDefault();
                event.dataTransfer.dropEffect = "copy";
              }
            }}
            onDrop={event => {
              if (canDrop && event.dataTransfer.getData(SPEED_SCREEN_FORM_DRAG_TYPE) === draggedForm) {
                event.preventDefault();
                placeForm(draggedForm, windowId);
              }
              onDragEnd();
            }}
            sx={{
              minWidth: 0,
              minHeight: 0,
              height: window.collapsed ? 48 : "100%",
              alignSelf: "start",
              display: expandedWindowId !== null && expandedWindowId !== windowId ? "none" : "block",
              outline: "none",
              border: "1px solid",
              borderColor: activeWindowId === windowId ? "primary.main" : "#d8ddea",
              "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
            }}
          >
            <MultiAnalysisPanel
              titleId={titleId}
              sx={{ height: "100%", border: 0 }}
              actions={
                <>
                  <IconButton
                    size="small"
                    aria-label={`${formatMessage({
                      id: window.collapsed
                        ? "pages.multiAnalysis.graphicArea.restoreWindow"
                        : "pages.multiAnalysis.graphicArea.minimizeWindow",
                    })}: ${label}`}
                    onClick={event => {
                      event.stopPropagation();
                      toggleCollapse(windowId);
                    }}
                  >
                    {window.collapsed ? <UnfoldMore fontSize="small" /> : <Minimize fontSize="small" />}
                  </IconButton>
                  {!window.collapsed && windows.length > 1 ? (
                    <IconButton
                      size="small"
                      aria-label={`${formatMessage({
                        id:
                          expandedWindowId === windowId
                            ? "pages.multiAnalysis.graphicArea.collapseWindow"
                            : "pages.multiAnalysis.graphicArea.expandWindow",
                      })}: ${label}`}
                      onClick={event => {
                        event.stopPropagation();
                        toggleExpand(windowId);
                      }}
                    >
                      {expandedWindowId === windowId ? (
                        <CloseFullscreen fontSize="small" />
                      ) : (
                        <OpenInFull fontSize="small" />
                      )}
                    </IconButton>
                  ) : null}
                </>
              }
            >
              {!window.collapsed ? (
                <FormWindow
                  strategies={strategies}
                  formId={window.form}
                  windowId={windowId}
                  canDrop={canDrop}
                  fallbackFormId={fallbackFormId}
                />
              ) : null}
            </MultiAnalysisPanel>
          </Box>
        );
      })}
    </Box>
  );
};
